export default {
    data() {
        return {
            activeIframe: null,
            iframes: new Map(),
            visibilityChangeHandler: null,
            blurHandler: null,
            insertionObserver: null,
            trackingInited: false,
        }
    },

    methods: {
        initAdTracking() {
            if (typeof window === 'undefined' || this.trackingInited) return
            this.trackingInited = true

            this.watchForIframeInsertion()
            this.setupVisibilityTracking()
            this.setupBlurTracking()
        },

        watchForIframeInsertion() {
            if (typeof MutationObserver === 'undefined') return
            if (this.insertionObserver) return

            const root = this.$el || document.body
            this.insertionObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => this.inspectAddedNode(node))
                })
            })

            this.insertionObserver.observe(root, {
                childList: true,
                subtree: true,
            })
        },

        inspectAddedNode(node) {
            if (!node || node.nodeType !== 1) return

            if (node.tagName === 'IFRAME' && node.closest('ins.adsbygoogle')) {
                this.setupIframeTracking(node)
                return
            }

            const iframesInNode = node.querySelectorAll?.('iframe') || []
            iframesInNode.forEach((iframe) => {
                if (iframe.closest('ins.adsbygoogle')) {
                    this.setupIframeTracking(iframe)
                }
            })
        },

        setupIframeTracking(iframe) {
            if (!iframe || this.iframes.has(iframe)) return

            const adSlot = iframe.closest('ins.adsbygoogle')?.dataset?.adSlot
            this.iframes.set(iframe, {
                hasTracked: false,
                adSlot,
            })
        },

        setupBlurTracking() {
            this.blurHandler = () => {
                const active = document.activeElement
                if (active && active.tagName === 'IFRAME' && this.iframes.has(active)) {
                    this.handleIframeFocus(active)
                }
            }
            window.addEventListener('blur', this.blurHandler)
        },

        handleIframeFocus(iframe) {
            const data = this.iframes.get(iframe)
            if (!data || data.hasTracked) return

            this.trackAdClick(iframe)
            data.hasTracked = true
            this.activeIframe = iframe
        },

        trackAdClick(iframe) {
            const container = iframe.closest('ins.adsbygoogle')
            if (!container) return

            const rect = container.getBoundingClientRect()
            const clickData = {
                insX: rect.left,
                insY: rect.top,
                insHeight: rect.height,
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
                adSlot: container.dataset.adSlot,
            }

            this.$adClick?.(JSON.stringify(clickData))
            this.$eventrack?.('ad_iframe_click', 'click', {
                'data-ad-slot': clickData.adSlot,
            })

            this.trackThirdPartyEvents('click', container.dataset.adSlot, clickData)
        },

        setupVisibilityTracking() {
            this.visibilityChangeHandler = () => {
                if (document.visibilityState === 'visible') {
                    this.activeIframe = null
                    return
                }

                if (this.activeIframe) {
                    const data = this.iframes.get(this.activeIframe)
                    this.$eventrack?.('ad_iframe_click_leave', 'click', {
                        'data-ad-slot': data?.adSlot,
                    })
                    this.trackThirdPartyEvents('click_leave', data?.adSlot, null)
                }
            }

            document.addEventListener('visibilitychange', this.visibilityChangeHandler)
        },

        cleanupAdTracking() {
            if (this.visibilityChangeHandler) {
                document.removeEventListener('visibilitychange', this.visibilityChangeHandler)
                this.visibilityChangeHandler = null
            }

            if (this.blurHandler) {
                window.removeEventListener('blur', this.blurHandler)
                this.blurHandler = null
            }

            if (this.insertionObserver) {
                this.insertionObserver.disconnect()
                this.insertionObserver = null
            }

            this.iframes.clear()
            this.activeIframe = null
            this.trackingInited = false
        },

        trackThirdPartyEvents(eventType, adSlot, transformData) {
            if (!adSlot) return

            if (eventType === 'click' && transformData) {
                this.trackFacebookViewContent(adSlot, transformData)
            }

            this.ttTrack(adSlot, eventType === 'click_leave' ? 'click' : eventType, `ad_iframe_${eventType}`)
        },

        trackFacebookViewContent(adSlot, transformData) {
            const facebookConfig = this.$store?.state?.hostInject?.facebook
            if (!facebookConfig?.pixelId) return

            const contentData = {
                content_type: 'advertisement',
                content_ids: [adSlot],
                content_name: `Ad Slot ${adSlot}`,
                content_category: 'advertisement',
                currency: 'USD',
                ad_position: {
                    x: transformData.insX,
                    y: transformData.insY,
                    height: transformData.insHeight,
                },
                viewport_size: {
                    width: transformData.windowWidth,
                    height: transformData.windowHeight,
                },
            }

            this.$fbViewContent?.(contentData)
        },

        ttTrack() {
            try {
                if (typeof ttq === 'undefined') return
                const eventName = this.$store?.state?.hostInject?.ttEvent || 'ClickButton'
                ttq.track(eventName)
            } catch (error) {
                console.error('[AdTracking] TikTok tracking error:', error)
            }
        },
    },

    beforeDestroy() {
        this.cleanupAdTracking()
    },
}
